import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateMain } from '@hero/core';
import type { Billing, BillingStatus } from '@hero/schema';
import { cn } from '@hero/web-lib';
import { ArrowRightIcon, FileTextIcon } from '@hero/web-icons';
import { Button } from '../../../ui/Button/Button';
import { Price } from '../../../app/Price';
import { BillingStatusBadge } from '../BillingStatusBadge';

export interface RecentBillingsListProps {
  billings: Billing[];
  /** Click a row → go to the Billing detail page. */
  onSelect?: (billing: Billing) => void;
  /**
   * Click the inline "Pay" pill on a payable row → open
   * `billing.checkout.url`. When omitted, no pay button is rendered
   * (e.g. on the admin panel's read-only Billing list).
   */
  onPay?: (billing: Billing) => void;
  /** Show all billings (typically links to /billing/history). */
  onViewAll?: () => void;
  /** Max items to show in the compact list. Default: 5. */
  limit?: number;
  isLoading?: boolean;
}

/**
 * Compact "recent billings" list intended for the BillingOverviewPage.
 * Heavier table lives in `BillingTable` (used by the History page + admin).
 *
 * Styled to match the checkout card visual language (rounded-2xl shell,
 * primary-tinted icon, individual row pills with hover affordance).
 */
export function RecentBillingsList({
  billings,
  onSelect,
  onPay,
  onViewAll,
  limit = 5,
  isLoading,
}: RecentBillingsListProps) {
  const { t } = useTranslation('m-billing');
  const visible = billings.slice(0, limit);

  return (
    <section
      className={cn(
        'flex flex-col gap-5 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm',
      )}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <FileTextIcon className="size-5" />
          </div>
          <div className="flex flex-col gap-0.5">
            <h3 className="text-base font-semibold text-gray-900">
              {t('billing_doc.title')}
            </h3>
          </div>
        </div>
        {onViewAll && (
          <Button variant="ghost" size="sm" onClick={onViewAll}>
            {t('page.tabs.history')}
            <ArrowRightIcon className="size-3.5 rtl:rotate-180" />
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="h-24 w-full animate-pulse rounded-xl bg-gray-100" />
      ) : visible.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/60 px-4 py-6 text-center">
          <p className="text-sm text-gray-500">{t('billing_doc.empty.title')}</p>
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {visible.map(b => {
            const interactive = !!onSelect;
            const showPay = !!onPay && isPayable(b.status) && !!b.checkout?.url;
            return (
              <li
                key={String(b._id)}
                onClick={interactive ? () => onSelect?.(b) : undefined}
                className={cn(
                  'flex items-center justify-between gap-3 rounded-xl border border-gray-100 bg-gray-50/50 px-4 py-3 transition',
                  interactive &&
                    'cursor-pointer hover:border-primary/30 hover:bg-primary/[0.03]',
                )}>
                <div className="flex flex-col gap-0.5 min-w-0">
                  <span className="truncate text-sm font-medium text-gray-900">
                    {b.title}
                  </span>
                  <span className="text-xs text-gray-500">
                    {b.issuedAt
                      ? formatDateMain({ date: b.issuedAt, format: 'date' })
                      : '—'}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold tabular-nums text-gray-900">
                    <Price amount={b.amount.total} currency={b.amount.currency} />
                  </span>
                  <BillingStatusBadge status={b.status} />
                  {showPay && (
                    <Button
                      size="sm"
                      variant="default"
                      className="shrink-0"
                      onClick={event => {
                        // Don't bubble into the row's onSelect → otherwise
                        // we'd both open the billing detail page AND open
                        // the Moyasar tab.
                        event.stopPropagation();
                        onPay?.(b);
                      }}>
                      {t('billing_doc.detail.pay_now')}
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function isPayable(status: BillingStatus): boolean {
  return (
    status === 'pending' || status === 'partially_paid' || status === 'past_due'
  );
}
